// Constants
const BASE_URL = "https://be.stage.helios-eex.com/stag/api";
//const BASE_URL = "http://localhost:8080/stag/api";

// Session token redeemed from the host app's one-shot exchange code (?code=). Kept in
// memory only: never written to storage, never put back into the URL.
let sessionToken = null;

/**
 * Redeem the ?code= the host app (helios-app-web building-eem "Open Mitigation Planner")
 * launched this planner with. The code is valid for 60 s and for exactly one redeem, so it
 * is exchanged once at load and scrubbed from the URL. Resolves to the token, or null when
 * there is no code (the ?token= fallback below then applies for one release).
 * @returns {Promise<string|null>}
 */
const redeemHandoffCode = async () => {
  const code = getQueryParam("code");
  if (!code) {
    return null;
  }

  try {
    const response = await fetch(`${BASE_URL}/authenticate/redeemHandoff`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (!data || !data.token) {
      throw new Error("redeemHandoff returned no token");
    }
    sessionToken = data.token;
    return sessionToken;
  } catch (error) {
    console.error("Error redeeming Helios hand-off code:", error);
    return null;
  } finally {
    scrubQueryParam("code");
  }
};

/**
 * Resolve the Helios session token for a request.
 * Order: caller argument, the token redeemed from ?code=, then (one release of fallback) the
 * legacy ?token= / route segment the host app used to launch this planner with. There is no
 * built-in fallback token.
 * @param {string|null} token - Optional token override
 * @returns {string} - The token to send in the helios-token header
 * @throws {Error} - When no token is available
 */
const requireToken = (token = null) => {
  const finalToken = token || sessionToken || getTokenFromQuery();

  if (!finalToken) {
    throw new Error("Helios session token missing");
  }

  return finalToken;
};

/**
 * Remove a query parameter from the visible URL (plain and hash-based query strings)
 * without reloading, so a one-shot code does not survive in history/bookmarks.
 * @param {string} paramName
 */
const scrubQueryParam = (paramName) => {
  try {
    const url = new URL(window.location.href);
    let changed = false;

    if (url.searchParams.has(paramName)) {
      url.searchParams.delete(paramName);
      changed = true;
    }

    const hashParts = (url.hash || "").split("?");
    if (hashParts.length > 1) {
      const hashParams = new URLSearchParams(hashParts.slice(1).join("?"));
      if (hashParams.has(paramName)) {
        hashParams.delete(paramName);
        const rest = hashParams.toString();
        url.hash = hashParts[0] + (rest ? `?${rest}` : "");
        changed = true;
      }
    }

    if (changed) {
      window.history.replaceState(window.history.state, document.title, url.toString());
    }
  } catch (error) {
    console.error("Could not scrub query parameter:", error);
  }
};


/**
 * Fetch project measures from Helios API
 * @param {number} projectId - The EEM Project ID
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Number of records to fetch
 * @param {number} params.start - Starting index
 * @param {string|null} params.query - Query filter
 * @param {string|null} params.sort - Sort parameter
 * @param {string} token - Optional token override
 * @returns {Promise<Object>} - API response data
 */
export const fetchProjectMeasures = async (
    projectId,
    params = {limit: 100, start: 0, query: null, sort: null},
    token = null
) => {
 const {limit, start, query, sort} = params;

  // Read token from the caller, the redeemed hand-off session, or the query string
  await sessionReady;
  const finalToken = requireToken(token);


  const url = `${BASE_URL}/eemProject/${projectId}/measures?limit=${limit}&query=${query}&sort=${sort}&start=${start}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "helios-token": finalToken,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching project measures:", error);
    throw error;
  }
};

/**
 * Get query parameter from URL
 * Handles both regular query strings and hash-based routing
 * @param {string} paramName - The parameter name to retrieve
 * @returns {string|null} - Parameter value or null
 */
const getQueryParam = (paramName) => {
  // First try regular query string (e.g., ?token=xxx)
  let params = new URLSearchParams(window.location.search);
  let value = params.get(paramName);

  // If not found, try hash-based query string (e.g., #/path?token=xxx)
  if (!value && window.location.hash) {
    const hashParts = window.location.hash.split('?');
    if (hashParts.length > 1) {
      params = new URLSearchParams(hashParts[1]);
      value = params.get(paramName);
    }
  }

  return value;
};

/**
 * Get parameter value from route path segments.
 * Supports patterns like:
 * - /.../token/<value>
 * - #/.../token/<value>
 * - /.../<paramName>/<value>
 * @param {string} paramName
 * @returns {string|null}
 */
const getRouteParam = (paramName) => {
  const extractFromPath = (path) => {
    if (!path) return null;

    const cleanPath = path.split("?")[0];
    const segments = cleanPath.split("/").filter(Boolean);
    const key = String(paramName).toLowerCase();

    for (let i = 0; i < segments.length - 1; i += 1) {
      if (segments[i].toLowerCase() === key) {
        const value = segments[i + 1];
        return value ? decodeURIComponent(value) : null;
      }
    }

    return null;
  };

  // Try normal pathname first
  let value = extractFromPath(window.location.pathname);
  if (value) return value;

  // Then try hash route path (e.g. #/svar/basic/default/token/xxxx)
  const hash = window.location.hash || "";
  const hashPath = hash.startsWith("#") ? hash.slice(1) : hash;
  value = extractFromPath(hashPath);
  return value;
};

// Started once at module load; every API helper awaits it before picking a token so that
// components mounting during the redeem do not race it. This has to sit BELOW the URL
// helpers it calls (getQueryParam / scrubQueryParam): they are const arrow functions, so
// invoking the redeem any earlier hits the temporal dead zone and rejects every request.
const sessionReady = redeemHandoffCode();

/**
 * Get token from URL query parameters
 * Handles both regular query strings and hash-based routing
 * @returns {string|null} - Token from URL or null
 */
export const getTokenFromQuery = () => {
  const routeToken = getRouteParam("token");
  const queryToken = getQueryParam("token");
  const token = routeToken || queryToken;


  return token;
};

/**
 * Get projectId from URL query parameters
 * Handles both regular query strings and hash-based routing
 * @returns {number|null} - Project ID from URL or null
 */
export const getProjectIdFromQuery = () => {
  const projectId = getQueryParam("projectId");
  return projectId ? parseInt(projectId, 10) : null;
};
/**
 * Fetch existing Gantt data from backend
 * @param {number} projectId - The EEM Project ID
 * @returns {Promise<Object|null>} - Gantt data or null if not found
 */
export const fetchGanttData = async (projectId) => {
  await sessionReady;
  const finalToken = requireToken();


  const url = `${BASE_URL}/eemPhase/${projectId}/gantt`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "helios-token": finalToken,
      },
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching Gantt data:", error);
    throw error;
  }
};

/**
 * Save Gantt structure (phases and measures) to backend
 * @param {number} projectId - The EEM Project ID
 * @param {Object} ganttData - The gantt structure with phases and measures
 * @param {Array} ganttData.phases - Array of phase objects
 * @param {Array} ganttData.measures - Array of measure objects
 * @returns {Promise<Object>} - API response data
 */
export const saveGanttStructure = async (projectId, ganttData) => {
  await sessionReady;
  const finalToken = requireToken();


  const url = `${BASE_URL}/eemPhase/${projectId}/gantt`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "helios-token": finalToken,
      },
      body: JSON.stringify(ganttData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error saving Gantt structure:", error);
    throw error;
  }
};

/**
 * Recalculate Phased EEM for a project
 * @param {number} projectId - The EEM Project ID
 * @returns {Promise<Object>} - API response data
 */
export const reCalculatePhasedEem = async (projectId) => {
  await sessionReady;
  const finalToken = requireToken();


  const url = `${BASE_URL}/eemPhase/${projectId}/reCalculatePhasedEem`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "helios-token": finalToken,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error recalculating Phased EEM:", error);
    throw error;
  }
};
